import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography
} from '@material-ui/core';
import { AppSnackbar } from '@sierralabs/react-material-ui';

import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import {
  useFindProductUserNeeds,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  BaseProductEntityTableProps,
  ProductComponent,
  ProductUserNeed
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';
import { stripHtml } from '../../../common/formatter';

const UserNeedsTable: React.FC<BaseProductEntityTableProps> = (
  props: BaseProductEntityTableProps
) => {
  const { productId, apiParams, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);

  const {
    productUserNeedList,
    isLoadingProductUserNeeds,
    loadingProductUserNeedsError,
    findProductUserNeeds
  } = useFindProductUserNeeds(productId);

  const findProductUserNeedsWithParams = useCallback(() => {
    findProductUserNeeds({ page, limit, order, direction, ...apiParams });
  }, [findProductUserNeeds, page, limit, order, direction, apiParams]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductUserNeedsWithParams;
    }
    findProductUserNeedsWithParams();
  }, [fetchRef, findProductUserNeedsWithParams]);

  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.UserNeed
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };
  console.log('productUserNeedList', productUserNeedList);
  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productUserNeed.id'}
                  direction={getSortDirection('productUserNeed.id')}
                  onClick={() => handleSort('productUserNeed.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productUserNeed.title'}
                  direction={getSortDirection('productUserNeed.title')}
                  onClick={() => handleSort('productUserNeed.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productUserNeed.description'}
                  direction={getSortDirection('productUserNeed.description')}
                  onClick={() => handleSort('productUserNeed.description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productUserNeed.createdBy'}
                  direction={getSortDirection('productUserNeed.createdBy')}
                  onClick={() => handleSort('productUserNeed.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'components.name'}
                  direction={getSortDirection('components.name')}
                  onClick={() => handleSort('components.name')}
                >
                  Component
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'release.name'}
                  direction={getSortDirection('release.name')}
                  onClick={() => handleSort('release.name')}
                >
                  Release
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductUserNeeds && (
              <TableRow>
                <TableCell colSpan={7} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productUserNeedList?.data?.map((userNeed: ProductUserNeed) => (
              <TableRow
                hover
                key={userNeed.id}
                onClick={() =>
                  history.push(
                    `/portal/products/${productId}/user-needs/${userNeed.id}`
                  )
                }
              >
                <TableCell>{prefix + userNeed.id}</TableCell>
                <WrappedTableCell>{userNeed.title}</WrappedTableCell>
                <WrappedTableCell>
                  <div className='truncate'>
                    {stripHtml(userNeed.description || '')}
                  </div>
                </WrappedTableCell>
                <TableCell>
                  {`${userNeed.task.createdBy?.firstName} ${userNeed.task.createdBy?.lastName}`}
                </TableCell>
                <TableCell>
                  {userNeed.task.user
                    ? `${userNeed.task.user?.firstName} ${userNeed.task.user.lastName}`
                    : 'No User Assigned'}
                </TableCell>
                <TableCell>
                  <TaskStatusLabel task={userNeed.task} />
                </TableCell>
                <TableCell>
                  {userNeed.components.length !== 0
                    ? userNeed.components
                        .map((component: ProductComponent) => component.name)
                        .join(', ')
                    : 'No Components'}
                </TableCell>
                <TableCell>
                  {userNeed.release ? userNeed.release.name : 'No Release'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={productUserNeedList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      <AppSnackbar
        severity='error'
        open={Boolean(loadingProductUserNeedsError)}
      >
        There was an error loading the list of{' '}
        {getProductEntityName('userNeed')}.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default UserNeedsTable;
